<template>
  <main>
    <b-overlay :show="loading">
      <b-card-actions
        :no-actions="true"
        :title="isEdit ? 'Ubah Catatan Analisis' : 'Tambah Catatan Analisis'"
      >
        <p class="mb-2 text-body">
          Catatan pengamatan sales untuk konsumen target yang belum deal —
          catat info konsumen dan brand/harga yang diamati.
        </p>

        <validation-observer ref="form">
          <b-row>
            <b-col cols="12">
              <h5 class="mb-1">Info Konsumen</h5>
            </b-col>

            <b-col sm="12" md="6">
              <b-form-group
                label="Pilih Sales"
                label-for="v-karyawan_id"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Sales"
                  :rules="{ required: true }"
                >
                  <b-form-select
                    id="v-karyawan_id"
                    v-model="form.karyawan_id"
                    name="karyawan_id"
                    :state="errors.length > 0 ? false : null"
                    :options="karyawanOptions"
                    :disabled="isSalesLocked"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="6">
              <b-form-group label="Tanggal & Waktu" label-for="v-tanggal">
                <validation-provider
                  #default="{ errors }"
                  name="Tanggal"
                  :rules="{ required: true }"
                >
                  <flat-pickr
                    id="v-tanggal"
                    v-model="form.tanggal"
                    class="form-control"
                    :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="6">
              <b-form-group
                label="Nama Customer / Konsumen"
                label-for="v-nama_costumer"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Nama konsumen"
                  rules="required"
                >
                  <b-form-input
                    id="v-nama_costumer"
                    v-model="form.nama_costumer"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Isi nama customer / konsumen"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="6">
              <b-form-group label="Tipe Konsumen" label-for="v-tipe_konsumen">
                <validation-provider
                  #default="{ errors }"
                  name="Tipe konsumen"
                  :rules="{ required: true }"
                >
                  <b-form-select
                    id="v-tipe_konsumen"
                    v-model="form.tipe_konsumen"
                    name="tipe_konsumen"
                    :state="errors.length > 0 ? false : null"
                    :options="tipeOptions"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col sm="12" md="6">
              <b-form-group label="Kecamatan" label-for="v-kecamatan">
                <validation-provider
                  #default="{ errors }"
                  name="Kecamatan"
                  rules="required"
                >
                  <b-form-input
                    id="v-kecamatan"
                    v-model="form.kecamatan"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Isi kecamatan"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group label="Alamat" label-for="v-alamat">
                <validation-provider
                  #default="{ errors }"
                  name="Alamat"
                  rules="required"
                >
                  <b-form-textarea
                    id="v-alamat"
                    v-model="form.alamat"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Isi alamat"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <hr class="my-2" />

          <b-row class="align-items-center mb-1">
            <b-col>
              <h5 class="mb-0">Brand Diamati</h5>
              <small class="text-muted">
                Brand dan harga jual yang diamati di lokasi konsumen.
              </small>
            </b-col>
            <b-col cols="auto">
              <b-button
                variant="primary"
                size="sm"
                :disabled="loading || removing"
                @click.prevent="addBrand"
              >
                <feather-icon icon="PlusIcon" class="mr-50" />
                Tambah Brand
              </b-button>
            </b-col>
          </b-row>

          <div
            v-if="!selectedBrands.length"
            class="text-center text-muted py-3 border rounded mb-2"
          >
            Belum ada brand. Klik <strong>Tambah Brand</strong> untuk menambah
            catatan.
          </div>

          <b-table
            v-else
            :fields="tableFields"
            :items="selectedBrands"
            hover
            bordered
            striped
            small
            responsive
            class="mb-2"
          >
            <template #cell(no)="{ index }">
              {{ index + 1 }}
            </template>

            <template #cell(brand)="{ item }">
              <b-form-input
                v-model="item.brand"
                placeholder="Nama brand"
                size="sm"
              />
            </template>

            <template #cell(harga)="{ item, index }">
              <b-form-input
                v-model="item.harga"
                placeholder="Harga"
                size="sm"
                @keyup="doFormatRupiah(index)"
              />
            </template>

            <template #cell(action)="{ index, item }">
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="loading || removing"
                v-b-tooltip.hover.top="'Hapus'"
                @click.prevent="removeBrand(index, item)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </template>
          </b-table>

          <small v-if="brandError" class="text-danger d-block mb-1">
            {{ brandError }}
          </small>

          <div class="d-flex justify-content-end mt-2">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="loading || removing"
              @click.prevent="goBack"
            >
              Batal
            </b-button>
            <b-button
              variant="primary"
              :disabled="loading || removing"
              @click.prevent="formSubmitted"
            >
              <b-spinner v-if="loading" small class="mr-50" />
              {{ isEdit ? "Ubah" : "Simpan" }}
            </b-button>
          </div>
        </validation-observer>
      </b-card-actions>
    </b-overlay>
  </main>
</template>

<script>
import flatPickr from "vue-flatpickr-component";
import BCardActions from "@core/components/b-card-actions/BCardActions.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  BRow,
  BCol,
  BFormSelect,
  BOverlay,
  BButton,
  BTable,
  BFormTextarea,
  BFormGroup,
  BFormInput,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import { extend } from "vee-validate";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import "@core/scss/vue/libs/vue-flatpicker.scss";

extend("required", {
  ...required,
  message: "{_field_} wajib diisi",
});

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    BFormSelect,
    BRow,
    flatPickr,
    BOverlay,
    BTable,
    BCardActions,
    BButton,
    BFormTextarea,
    BCol,
    BFormGroup,
    BFormInput,
    BSpinner,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      loading: false,
      removing: false,
      selectedBrands: [],
      brandError: "",
      form: {
        karyawan_id: "",
        tanggal: new Date(),
        nama_costumer: "",
        alamat: "",
        kecamatan: "",
        tipe_konsumen: null,
      },
      tipeOptions: [],
      karyawanOptions: [],
      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      tableFields: [
        { key: "no", label: "No", thStyle: { width: "48px" } },
        { key: "brand", label: "Brand" },
        { key: "harga", label: "Harga / Selling" },
        { key: "action", label: "Aksi", thStyle: { width: "72px" } },
      ],
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    },
    isSalesLocked() {
      return this.userData && this.userData.level && this.userData.level.id == 7;
    },
  },
  methods: {
    goBack() {
      this.$router.push("/analisis");
    },
    doFormatRupiah(index) {
      const row = this.selectedBrands[index];
      if (!row) return;
      row.harga = this.formatRupiah(row.harga);
    },
    addBrand() {
      this.brandError = "";
      this.selectedBrands.push({
        brand: "",
        harga: "",
      });
    },
    async removeBrand(index, item) {
      this.brandError = "";
      if (!item.id) {
        this.selectedBrands.splice(index, 1);
        return;
      }
      try {
        this.removing = true;
        await this.$store.dispatch("analisis/saveBrand", [
          { id: item.id, fungsi: 2 },
        ]);
        this.selectedBrands.splice(index, 1);
        this.displaySuccess({ message: "Brand berhasil dihapus" });
      } catch (e) {
        this.displayError(e);
      } finally {
        this.removing = false;
      }
    },
    getsales_id() {
      return this.$store
        .dispatch("karyawan/getData", {})
        .then(() => {
          const karyawan = JSON.parse(
            JSON.stringify(this.$store.state.karyawan.datas || [])
          );
          const sales = this.filterByJabatan(karyawan, "sales");
          sales.forEach((item) => {
            item.value = item.id;
            item.text = item.nama_lengkap;
          });
          this.karyawanOptions = sales;
        })
        .catch((e) => {
          this.displayError(e);
        });
    },
    gettipe() {
      return this.$store
        .dispatch("tipekonsumen/getData", {})
        .then(() => {
          const tipekonsumen = JSON.parse(
            JSON.stringify(this.$store.state.tipekonsumen.datas || [])
          );
          tipekonsumen.forEach((item) => {
            item.value = item.id;
            item.text = item.nama;
          });
          this.tipeOptions = tipekonsumen;
        })
        .catch((e) => {
          this.displayError(e);
        });
    },
    normalizeAnalisis(payload) {
      if (!payload) return null;
      if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
        return payload.data;
      }
      return payload;
    },
    async getAnalisis() {
      try {
        this.loading = true;
        const raw = await this.$store.dispatch(
          "analisis/getDataById",
          this.$route.params.id
        );
        const analisis = this.normalizeAnalisis(raw);
        if (!analisis) {
          this.displayError({ message: "Data tidak ditemukan" });
          this.$router.push({ name: "route-analisis" });
          return;
        }

        const karyawanId =
          (analisis.karyawan && analisis.karyawan.id) ||
          analisis.karyawan_id ||
          this.form.karyawan_id;

        this.form.karyawan_id = karyawanId;
        this.form.tanggal = analisis.tanggal;
        this.form.nama_costumer = analisis.nama_costumer || "";
        this.form.alamat = analisis.alamat || "";
        this.form.kecamatan = analisis.kecamatan || "";
        this.form.tipe_konsumen =
          (analisis.tipe && analisis.tipe.id) ||
          analisis.tipe_konsumen ||
          null;

        const brands = Array.isArray(analisis.brand) ? analisis.brand : [];
        this.selectedBrands = brands.map((b) => ({
          ...b,
          harga:
            b.harga !== null && b.harga !== undefined && b.harga !== ""
              ? this.formatRupiah(String(b.harga))
              : "",
        }));
      } catch (e) {
        this.displayError(e);
        this.$router.push({ name: "route-analisis" });
      } finally {
        this.loading = false;
      }
    },
    validateBrands() {
      const filled = this.selectedBrands.filter(
        (item) => item.brand && String(item.brand).trim() !== ""
      );
      if (!filled.length) {
        this.brandError = "Minimal satu brand dengan nama wajib diisi";
        return false;
      }
      this.brandError = "";
      return true;
    },
    async formSubmitted() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;
      if (!this.validateBrands()) return;

      const brands = this.selectedBrands
        .filter((item) => item.brand && String(item.brand).trim() !== "")
        .map((item) => {
          const row = { ...item };
          if (this.isEdit) {
            row.id_analisis = this.$route.params.id;
          }
          row.harga = this.unFormatRupiah(row.harga);
          return row;
        });

      const payload = {
        karyawan_id: this.form.karyawan_id,
        tanggal: this.form.tanggal,
        nama_costumer: this.form.nama_costumer,
        kecamatan: this.form.kecamatan,
        alamat: this.form.alamat,
        tipe_konsumen: this.form.tipe_konsumen,
        brand: brands,
      };
      if (this.isEdit) {
        payload.id = this.$route.params.id;
      }

      try {
        this.loading = true;
        await this.$store.dispatch("analisis/save", [payload]);
        this.displaySuccess({
          message: this.isEdit
            ? "Catatan analisis berhasil diubah"
            : "Catatan analisis berhasil disimpan",
        });
        this.$router.push("/analisis");
      } catch (e) {
        this.displayError(e);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    if (this.userData && this.userData.karyawan && this.userData.karyawan.id) {
      this.form.karyawan_id = this.userData.karyawan.id;
    }
  },
  async mounted() {
    await Promise.all([this.getsales_id(), this.gettipe()]);
    if (this.isEdit) {
      await this.getAnalisis();
    } else if (!this.selectedBrands.length) {
      this.addBrand();
    }
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
